import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { ChevronLeft, Trophy } from 'lucide-react';
import SpinWheel from './games/SpinWheel';
import MemoryMatch from './games/MemoryMatch';
import Flashcard from './games/Flashcard';
import QuizBattle from './games/QuizBattle';
import ScratchCard from './games/ScratchCard';
import LudoLearning from './games/LudoLearning';
import SnakeLadderLearning from './games/SnakeLadderLearning';
import KBCLearning from './games/KBCLearning';
import PulsePowerQuiz from './games/PulsePowerQuiz';
import PulseCrush from './games/PulseCrush';

const RENDERERS = {
    pulse_power_quiz: PulsePowerQuiz,
    pulse_crush: PulseCrush,
    spin_wheel: SpinWheel,
    memory_match: MemoryMatch,
    flashcard: Flashcard,
    quiz_battle: QuizBattle,
    scratch_card: ScratchCard,
    ludo_learning: LudoLearning,
    snake_ladder: SnakeLadderLearning,
    kbc_learning: KBCLearning,
};

export default function GameRoute() {
    const { code } = useParams();
    const [game, setGame] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        api.get(`/games/${code}`).then((r) => setGame(r.data)).catch(() => navigate('/learn/arcade'));
        api.get(`/games/${code}/leaderboard`).then((r) => setLeaderboard(r.data)).catch(() => setLeaderboard([]));
    }, [code, navigate]);

    const refresh = () => api.get(`/games/${code}/leaderboard`).then((r) => setLeaderboard(r.data)).catch(() => {});

    if (!game) return null;
    const Renderer = RENDERERS[code];

    // Pulse Power & Pulse Crush run full-screen: no back link, no title/description,
    // no leaderboard sidebar — just the immersive game.
    if (code === 'pulse_power_quiz' || code === 'pulse_crush') {
        return (
            <div data-testid={`game-page-${code}`} className="-mx-4 sm:-mx-6 lg:mx-0">
                {Renderer ? <Renderer game={game} onPlayed={refresh} /> : null}
            </div>
        );
    }

    return (
        <div data-testid={`game-page-${code}`}>
            <button onClick={() => navigate('/learn/arcade')} className="inline-flex items-center gap-1 text-xs text-white/60 mb-3 hover:text-white" data-testid="game-back-btn">
                <ChevronLeft className="h-3.5 w-3.5" /> Back to Arcade
            </button>
            <h1 className="font-display font-extrabold text-3xl text-white">{game.name}</h1>
            <p className="text-sm text-white/70 mt-1">{game.description}</p>

            <div className="grid lg:grid-cols-3 gap-6 mt-5">
                <div className="lg:col-span-2">
                    {Renderer ? <Renderer game={game} onPlayed={refresh} /> : <div className="text-slate-500 text-sm">Game coming soon.</div>}
                </div>
                <aside className="bg-white rounded-2xl border border-slate-200 p-4 h-fit" data-testid="game-leaderboard">
                    <h3 className="font-display font-bold text-slate-900 flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" /> Top Players</h3>
                    <div className="mt-3 space-y-2">
                        {leaderboard.map((p, i) => (
                            <div key={p.user_id} className="flex items-center gap-2 text-sm">
                                <div className="w-6 text-center font-display font-extrabold text-slate-700">#{i + 1}</div>
                                <div className="flex-1 truncate">{p.full_name}</div>
                                <div className="text-brand-blue font-bold">{p.best}</div>
                            </div>
                        ))}
                        {!leaderboard.length && <div className="text-xs text-slate-500">Be the first to play!</div>}
                    </div>
                </aside>
            </div>
        </div>
    );
}
